import { projectsProjectAccessDecisionSchema } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectOrderReferenceSnapshotSchema, projectsProjectShippingAddressSchema, } from '@crest/module-projects/contracts/project-order-reference';
import { z } from 'zod';
export const procurementOrdersCapability = 'procurement.orders.v1';
export const procurementOrderVersionsCapability = 'procurement.order-versions.v1';
export const procurementOrderNumberingCapability = 'procurement.order-numbering.v1';
export const procurementCostSummariesCapability = 'procurement.cost-summaries.v1';
export const procurementOrderIdSchema = z.string().uuid();
export const procurementOrderKindSchema = z.enum(['subcontract-work-order', 'purchase-order']);
export const procurementWorkOrderStatusSchema = z.enum([
    'draft', 'sent', 'in_progress', 'completed', 'cancelled',
]);
export const procurementPurchaseOrderStatusSchema = z.enum([
    'draft', 'ordered', 'partial_received', 'received', 'cancelled',
]);
export const procurementOrderStatusSchema = z.union([
    procurementWorkOrderStatusSchema,
    procurementPurchaseOrderStatusSchema,
]);
export const procurementCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const procurementQuantityTenThousandthsSchema = z.number().int().min(1).max(10_000_000);
export const procurementMoneyCentsSchema = z.number().int().min(0).max(999_999_999_999);
export const procurementTaxBasisPointsSchema = z.number().int().min(0).max(10_000);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const procurementOrderLineSourceReferenceSchema = z.strictObject({
    capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
    sourceId: z.string().trim().min(1).max(240),
    label: z.string().trim().min(1).max(240),
}).nullable();
export const procurementOrderLineInputSchema = z.strictObject({
    sourceReference: procurementOrderLineSourceReferenceSchema.default(null),
    name: z.string().trim().min(1).max(240),
    description: nullableText(2_000).default(null),
    quantityTenThousandths: procurementQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: procurementMoneyCentsSchema,
});
export const procurementOrderLineSchema = procurementOrderLineInputSchema
    .omit({ unitPriceCents: true })
    .extend({
    lineNumber: z.number().int().positive(),
    unitPriceCents: procurementMoneyCentsSchema.nullable(),
    lineTotalCents: procurementMoneyCentsSchema.nullable(),
});
export const procurementPurchaserReferenceSchema = z.strictObject({
    membershipId: z.string().uuid(),
    displayName: z.string().trim().min(1).max(240),
});
export const procurementCounterpartySnapshotSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(240),
    relationshipOrganizationId: z.string().uuid().nullable(),
    relationshipContactId: z.string().uuid().nullable(),
    phone: nullableText(80),
    email: z.string().trim().email().max(320).nullable(),
});
export const procurementOrderDraftSchema = z.strictObject({
    kind: procurementOrderKindSchema,
    projectId: z.string().uuid().nullable(),
    purchaserReference: procurementPurchaserReferenceSchema.nullable(),
    counterparty: procurementCounterpartySnapshotSchema,
    scopeDescription: nullableText(8_000),
    notes: nullableText(8_000),
    scheduledDate: z.string().date().nullable(),
    completedDate: z.string().date().nullable(),
    expectedDeliveryDate: z.string().date().nullable(),
    receivedDate: z.string().date().nullable(),
    shippingAddress: projectsProjectShippingAddressSchema.nullable(),
    currencyCode: procurementCurrencyCodeSchema.nullable(),
    taxBasisPoints: procurementTaxBasisPointsSchema.nullable(),
    items: z.array(procurementOrderLineInputSchema).min(1).max(250),
}).superRefine((draft, context) => {
    if (draft.kind === 'subcontract-work-order') {
        if (draft.projectId === null) {
            context.addIssue({ code: 'custom', path: ['projectId'], message: 'A subcontract work order requires a Project.' });
        }
        if (draft.purchaserReference !== null) {
            context.addIssue({ code: 'custom', path: ['purchaserReference'], message: 'A work order cannot carry a shop purchaser.' });
        }
        for (const field of ['expectedDeliveryDate', 'receivedDate', 'shippingAddress']) {
            if (draft[field] !== null) {
                context.addIssue({ code: 'custom', path: [field], message: 'Purchase-only data cannot be stored on a work order.' });
            }
        }
    }
    else {
        if (draft.projectId === null && draft.purchaserReference === null) {
            context.addIssue({ code: 'custom', path: ['purchaserReference'], message: 'A purchase order requires a Project or purchaser.' });
        }
        for (const field of ['scheduledDate', 'completedDate']) {
            if (draft[field] !== null) {
                context.addIssue({ code: 'custom', path: [field], message: 'Work-order-only dates cannot be stored on a purchase order.' });
            }
        }
    }
});
const procurementCommandReasonSchema = z.string().trim().min(10).max(500);
export const procurementCreateOrderCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    orderId: procurementOrderIdSchema,
    order: procurementOrderDraftSchema,
    reason: procurementCommandReasonSchema,
});
export const procurementReviseOrderCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    order: procurementOrderDraftSchema,
    reason: procurementCommandReasonSchema,
});
export const procurementTransitionOrderCommandSchema = z.strictObject({
    kind: z.literal('transition'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    targetStatus: procurementOrderStatusSchema,
    effectiveDate: z.string().date().nullable().default(null),
    reason: procurementCommandReasonSchema,
});
export const procurementOrderStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: procurementCommandReasonSchema,
});
export const procurementOrderCommandSchema = z.discriminatedUnion('kind', [
    procurementReviseOrderCommandSchema,
    procurementTransitionOrderCommandSchema,
    procurementOrderStateCommandSchema,
]);
export const procurementOrderPublicDataSchema = z.strictObject({
    projectReference: projectsProjectOrderReferenceSnapshotSchema.nullable(),
    projectAccessDecision: projectsProjectAccessDecisionSchema.nullable(),
    purchaserReference: procurementPurchaserReferenceSchema.nullable(),
    counterparty: procurementCounterpartySnapshotSchema,
    scopeDescription: nullableText(8_000),
    notes: nullableText(8_000),
    scheduledDate: z.string().date().nullable(),
    completedDate: z.string().date().nullable(),
    expectedDeliveryDate: z.string().date().nullable(),
    receivedDate: z.string().date().nullable(),
    shippingAddress: projectsProjectShippingAddressSchema.nullable(),
    currencyCode: procurementCurrencyCodeSchema,
    taxBasisPoints: procurementTaxBasisPointsSchema.nullable(),
    subtotalCents: procurementMoneyCentsSchema.nullable(),
    taxCents: procurementMoneyCentsSchema.nullable(),
    totalCents: procurementMoneyCentsSchema.nullable(),
    items: z.array(procurementOrderLineSchema),
});
export const procurementOrderVersionSchema = z.strictObject({
    contractVersion: z.literal(procurementOrderVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    orderId: procurementOrderIdSchema,
    versionNumber: z.number().int().positive(),
    status: procurementOrderStatusSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: procurementCommandReasonSchema,
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: procurementOrderPublicDataSchema,
});
export const procurementOrderSchema = z.strictObject({
    contractVersion: z.literal(procurementOrdersCapability),
    tenantId: z.string().uuid(),
    id: procurementOrderIdSchema,
    orderNumber: z.string().trim().min(1).max(80),
    kind: procurementOrderKindSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: procurementOrderVersionSchema,
});
export const procurementOrderListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    kind: procurementOrderKindSchema.nullable().default(null),
    status: procurementOrderStatusSchema.nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    purchaserMembershipId: z.string().uuid().nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
const procurementPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const procurementOrderResponseSchema = z.strictObject({
    data: procurementOrderSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const procurementOrdersResponseSchema = z.strictObject({
    data: z.array(procurementOrderSchema),
    page: procurementPageSchema,
});
export const procurementOrderVersionsResponseSchema = z.strictObject({
    data: z.array(procurementOrderVersionSchema),
});
export const procurementProjectCostSummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(procurementCostSummariesCapability),
        projectReference: projectsProjectOrderReferenceSnapshotSchema,
        orderCount: z.number().int().nonnegative(),
        openOrderCount: z.number().int().nonnegative(),
        currencyCode: procurementCurrencyCodeSchema.nullable(),
        subtotalCents: procurementMoneyCentsSchema.nullable(),
        taxCents: procurementMoneyCentsSchema.nullable(),
        totalCents: procurementMoneyCentsSchema.nullable(),
        byKind: z.array(z.strictObject({
            kind: procurementOrderKindSchema,
            orderCount: z.number().int().nonnegative(),
            totalCents: procurementMoneyCentsSchema.nullable(),
        })),
    }),
});
export function isProcurementStatusValidForKind(kind, status) {
    return kind === 'subcontract-work-order'
        ? procurementWorkOrderStatusSchema.safeParse(status).success
        : procurementPurchaseOrderStatusSchema.safeParse(status).success;
}
const transitions = {
    'subcontract-work-order': {
        draft: ['sent', 'cancelled'],
        sent: ['in_progress', 'cancelled'],
        in_progress: ['completed', 'cancelled'],
        completed: [],
        cancelled: [],
    },
    'purchase-order': {
        draft: ['ordered', 'cancelled'],
        ordered: ['partial_received', 'received', 'cancelled'],
        partial_received: ['received', 'cancelled'],
        received: [],
        cancelled: [],
    },
};
export function canTransitionProcurementOrder(kind, from, to) {
    return transitions[kind][from]?.includes(to) === true;
}
export function calculateProcurementOrderTotals(items, taxBasisPoints) {
    const parsedTax = procurementTaxBasisPointsSchema.parse(taxBasisPoints);
    const lineTotalsCents = items.map((item) => {
        const parsed = procurementOrderLineInputSchema.parse(item);
        const total = (BigInt(parsed.quantityTenThousandths) * BigInt(parsed.unitPriceCents) + 5000n) / 10000n;
        return procurementMoneyCentsSchema.parse(Number(total));
    });
    const subtotal = lineTotalsCents.reduce((sum, value) => sum + BigInt(value), 0n);
    const tax = (subtotal * BigInt(parsedTax) + 5000n) / 10000n;
    return {
        lineTotalsCents,
        subtotalCents: procurementMoneyCentsSchema.parse(Number(subtotal)),
        taxCents: procurementMoneyCentsSchema.parse(Number(tax)),
        totalCents: procurementMoneyCentsSchema.parse(Number(subtotal + tax)),
    };
}
//# sourceMappingURL=procurement-order.contract.js.map